import { DataTable, ServerErrorFallbackCard } from "@/components/ui";
import { MatterPropertyDamageContextMenu } from "@/components/features/matter/context-menus/MatterPropertyDamageContextMenu";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useDeleteMatterPropertyDamage } from "@/lib/data/react-query/matters/matter-property-damage";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { Matter } from "@/lib/types";
import { matterPropertyDamageColumns } from "./matter-property-damage-columns";
import { ErrorBoundary } from "@sentry/react";

interface MatterPropertyDamageTableProps {
  matter: Matter;
  tableButtons?: React.ReactNode;
}

const MatterPropertyDamageTableDangerous = ({
  matter,
  tableButtons,
}: MatterPropertyDamageTableProps) => {
  const { openPropertyDamageModal } = useGlobalModal();
  const { hasActionPermission } = useUser();
  const deletePropertyDamage = useDeleteMatterPropertyDamage();
  const canDeletePropertyDamage = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterPropertyDamage,
    ACTION_PERMISSION_VALUES.delete,
  );

  return (
    <DataTable
      entityLabelPlural="property damage records"
      columns={matterPropertyDamageColumns}
      data={matter.propertyDamages}
      onRowClick={(row) => {
        openPropertyDamageModal({
          matterId: matter.id,
          propertyDamageId: row.original.id,
          editing: true,
          initialTab: "details",
        });
      }}
      renderRowContextMenu={(row, trigger) => {
        const propertyDamage = row.original;
        const label = propertyDamage.label || "property damage";
        return (
          <MatterPropertyDamageContextMenu
            canDelete={canDeletePropertyDamage}
            id={propertyDamage.id}
            isDeleting={deletePropertyDamage.isPending}
            label={label}
            matter={matter}
            onEdit={() => {
              openPropertyDamageModal({
                matterId: matter.id,
                propertyDamageId: propertyDamage.id,
                editing: true,
                initialTab: "details",
              });
            }}
            onDelete={async () => {
              await deletePropertyDamage.mutateAsync({
                matterId: matter.id,
                id: propertyDamage.id,
              });
            }}
            propertyDamage={propertyDamage}
            trigger={trigger}
          />
        );
      }}
      tableName={tanstackTableNames.matterPropertyDamage}
      tableButtons={tableButtons}
      initialSort={{
        id: "Date Created",
        desc: true,
      }}
    />
  );
};

export const MatterPropertyDamageTable = (
  props: MatterPropertyDamageTableProps,
) => (
  <ErrorBoundary
    fallback={
      <ServerErrorFallbackCard message="Unable to load property damage table right now." />
    }
  >
    <MatterPropertyDamageTableDangerous {...props} />
  </ErrorBoundary>
);
